/**
 * ViewAsControl — a slim "Internal / Client" lens toggle for the top nav.
 *
 * Lets staff preview the app as a client anywhere in the app, reusing the same
 * global `useAdsViewStore` lens that the Ads Tools pages use (single source of
 * truth, already persisted). Client-gated UI (e.g. Quick Create's Bulk Generate)
 * checks `viewMode === 'client'` so operators can see the client experience
 * without a separate client login.
 *
 * Renders only for staff, and only OFF the `/ads-tools` routes — there the
 * fuller `AdsViewControls` already surfaces the same lens (plus tier preview),
 * so showing both would duplicate it.
 */

import { useLocation } from 'react-router-dom';
import { Eye, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { canAccessInternalAdsTools } from '@/lib/adsToolsAccess';
import { useAdsViewStore } from '@/stores/useAdsViewStore';

function LensButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-background shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
      aria-pressed={active}
    >
      {icon}
      {label}
    </button>
  );
}

export function ViewAsControl() {
  const location = useLocation();
  const userProfile = useWorkspaceStore((s) => s.userProfile);
  const viewMode = useAdsViewStore((s) => s.viewMode);
  const setViewMode = useAdsViewStore((s) => s.setViewMode);

  const isAdsToolsRoute = location.pathname.startsWith('/ads-tools');
  if (isAdsToolsRoute || !canAccessInternalAdsTools(userProfile)) return null;

  return (
    <div
      className="hidden md:inline-flex items-center rounded-md border border-border bg-muted p-0.5"
      title="Preview the app as an internal operator or as a client"
    >
      <LensButton
        active={viewMode === 'internal'}
        onClick={() => setViewMode('internal')}
        icon={<Eye className="h-3.5 w-3.5" />}
        label="Internal"
      />
      <LensButton
        active={viewMode === 'client'}
        onClick={() => setViewMode('client')}
        icon={<Users className="h-3.5 w-3.5" />}
        label="Client"
      />
    </div>
  );
}
